/**
 * WalletPage - Artist Wallet & Cash Out (Stripe Connect Express)
 * Shows Available / Pending / Lifetime balance, bank onboarding, cash-out, and history.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wallet, ArrowLeft, Landmark, Loader2, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle2, AlertCircle, ShieldCheck, DollarSign, Zap, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const WalletPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showCashOut, setShowCashOut] = useState(false);
  const [amount, setAmount] = useState('');
  const [cashingOut, setCashingOut] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoThreshold, setAutoThreshold] = useState('25');
  const [savingAuto, setSavingAuto] = useState(false);
  const [txFilter, setTxFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/wallet`, { headers: authHeaders() });
      setWallet(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/auth');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Sync auto cash-out settings from the wallet payload
  useEffect(() => {
    if (wallet) {
      setAutoEnabled(!!wallet.auto_cashout_enabled);
      setAutoThreshold(String(wallet.auto_cashout_threshold || 25));
    }
  }, [wallet]);

  const saveAuto = async (enabled, threshold) => {
    const minT = wallet?.min_cashout || 5;
    let t = parseFloat(threshold);
    if (!t || t < minT) t = minT;
    setSavingAuto(true);
    try {
      const res = await axios.put(`${API_URL}/api/wallet/auto-cashout`,
        { enabled, threshold: t }, { headers: authHeaders() });
      setAutoEnabled(res.data.auto_cashout_enabled);
      setAutoThreshold(String(res.data.auto_cashout_threshold));
      toast.success(enabled
        ? `Auto cash-out on — you'll be paid automatically above ${money(res.data.auto_cashout_threshold)}`
        : 'Auto cash-out turned off');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save auto cash-out');
    }
    setSavingAuto(false);
  };

  // Returning from Stripe onboarding — re-check status
  useEffect(() => {
    if (params.get('connect')) {
      axios.get(`${API_URL}/api/wallet/status`, { headers: authHeaders() })
        .then(() => load())
        .catch(() => {});
      if (params.get('connect') === 'return') toast.success('Bank connected! Verifying with Stripe…');
      params.delete('connect');
      setParams(params, { replace: true });
    }
  }, [params, setParams, load]);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/wallet/connect`, {}, { headers: authHeaders() });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start bank setup');
      setConnecting(false);
    }
  };

  const doCashOut = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < (wallet?.min_cashout || 5)) {
      toast.error(`Minimum cash out is ${money(wallet?.min_cashout || 5)}`);
      return;
    }
    if (amt > (wallet?.available || 0)) {
      toast.error('Amount exceeds your available balance');
      return;
    }
    setCashingOut(true);
    try {
      const res = await axios.post(`${API_URL}/api/wallet/cash-out`, { amount: amt }, { headers: authHeaders() });
      toast.success(`Cashed out ${money(res.data.amount)} to your bank! 🎉`);
      setShowCashOut(false);
      setAmount('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cash out failed');
    }
    setCashingOut(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" /></div>;
  }

  const canCashOut = wallet?.connected && wallet?.payouts_enabled && (wallet?.available || 0) >= (wallet?.min_cashout || 5);

  return (
    <div className="min-h-screen bg-[#050505] pb-28" data-testid="wallet-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B0A10]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="wallet-back" className="p-2 rounded-full hover:bg-white/10">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
            <Wallet className="w-6 h-6 text-[#00F0FF]" /> Wallet
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Available balance hero */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-6 bg-gradient-to-br from-[#FF007F]/20 via-[#12101A] to-[#00F0FF]/20 border border-white/10">
          <p className="text-[#A1A1AA] text-sm">Available to cash out</p>
          <p className="text-5xl font-bold text-white mt-1" data-testid="wallet-available">{money(wallet?.available)}</p>
          <div className="flex gap-6 mt-5">
            <div>
              <p className="text-[#71717A] text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</p>
              <p className="text-white font-semibold mt-0.5" data-testid="wallet-pending">{money(wallet?.pending)}</p>
            </div>
            <div>
              <p className="text-[#71717A] text-xs">Lifetime earned</p>
              <p className="text-white font-semibold mt-0.5" data-testid="wallet-lifetime">{money(wallet?.lifetime)}</p>
            </div>
            <div>
              <p className="text-[#71717A] text-xs">Withdrawn</p>
              <p className="text-white font-semibold mt-0.5" data-testid="wallet-withdrawn">{money(wallet?.withdrawn)}</p>
            </div>
          </div>
        </motion.div>

        {/* Payouts not configured (owner needs real Stripe key) */}
        {!wallet?.payouts_available && (
          <div className="rounded-2xl p-4 bg-amber-500/10 border border-amber-500/20 flex items-start gap-3" data-testid="payouts-unavailable">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Cash out is being set up</p>
              <p className="text-[#A1A1AA] text-sm mt-0.5">Your earnings are safe and tracked here. Bank payouts turn on once the platform finishes Stripe setup.</p>
            </div>
          </div>
        )}

        {/* Connect / Cash out actions */}
        {wallet?.payouts_available && (
          <div className="space-y-3">
            {!wallet?.connected || !wallet?.payouts_enabled ? (
              <button onClick={startOnboarding} disabled={connecting} data-testid="connect-bank-btn"
                className="w-full py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/90 disabled:opacity-60 transition-colors">
                {connecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Landmark className="w-5 h-5" />}
                {wallet?.connected ? 'Finish bank setup' : 'Link your bank to cash out'}
              </button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#39FF14]" data-testid="bank-connected">
                <ShieldCheck className="w-4 h-4" /> Bank connected — you're ready to cash out
              </div>
            )}

            <button onClick={() => { setAmount(String(wallet?.available || '')); setShowCashOut(true); }}
              disabled={!canCashOut} data-testid="cash-out-btn"
              className="w-full py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-90">
              <ArrowUpRight className="w-5 h-5" /> Cash Out
            </button>
            {wallet?.connected && wallet?.payouts_enabled && (wallet?.available || 0) < (wallet?.min_cashout || 5) && (
              <p className="text-[#71717A] text-xs text-center">You need at least {money(wallet?.min_cashout)} available to cash out.</p>
            )}
          </div>
        )}

        {/* Auto Cash-Out */}
        {wallet?.payouts_available && wallet?.connected && wallet?.payouts_enabled && (
          <div className="rounded-2xl p-4 bg-[#12101A] border border-white/10" data-testid="auto-cashout-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#00F0FF]/15 flex items-center justify-center">
                  <RefreshCw className="w-4.5 h-4.5 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Auto cash-out</p>
                  <p className="text-[#71717A] text-xs">Get paid automatically when you cross your threshold</p>
                </div>
              </div>
              <button
                onClick={() => saveAuto(!autoEnabled, autoThreshold)}
                disabled={savingAuto}
                data-testid="auto-cashout-toggle"
                aria-pressed={autoEnabled}
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${autoEnabled ? 'bg-[#39FF14]' : 'bg-white/15'} disabled:opacity-60`}
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${autoEnabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>

            {autoEnabled && (
              <div className="mt-4 space-y-3" data-testid="auto-cashout-settings">
                <div>
                  <label className="text-[#A1A1AA] text-xs">Pay me out automatically once my balance reaches</label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                      <input
                        type="number" min={wallet?.min_cashout} step="1"
                        value={autoThreshold}
                        onChange={(e) => setAutoThreshold(e.target.value)}
                        data-testid="auto-cashout-threshold"
                        className="w-full pl-7 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-[#00F0FF]/50"
                      />
                    </div>
                    <button
                      onClick={() => saveAuto(true, autoThreshold)}
                      disabled={savingAuto}
                      data-testid="auto-cashout-save"
                      className="px-4 py-2.5 rounded-xl bg-[#00F0FF] text-[#050505] font-semibold text-sm disabled:opacity-60 flex items-center gap-1.5"
                    >
                      {savingAuto ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => { setAutoThreshold(String(wallet?.min_cashout || 5)); saveAuto(true, wallet?.min_cashout || 5); }}
                  disabled={savingAuto}
                  data-testid="auto-cashout-instant"
                  className="w-full py-2.5 rounded-xl border border-[#FF007F]/40 text-[#FF007F] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#FF007F]/10 disabled:opacity-60"
                >
                  <Zap className="w-4 h-4" /> Instant — pay me the moment funds clear
                </button>
                <p className="text-[#71717A] text-xs">
                  We check every few hours and send your full available balance to your bank once it's at or above your threshold. Minimum {money(wallet?.min_cashout)}.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Transaction history */}
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" /> Activity</h3>
          {/* Filter chips */}
          {wallet?.transactions?.length > 0 && (
            <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar" data-testid="wallet-tx-filters">
              {[
                { id: 'all', label: 'All' },
                { id: 'in', label: 'Earnings' },
                { id: 'auto', label: 'Auto payouts' },
                { id: 'manual', label: 'Manual payouts' },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTxFilter(f.id)}
                  data-testid={`wallet-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${txFilter === f.id ? 'bg-white text-[#050505]' : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {(() => {
            const all = wallet?.transactions || [];
            const txns = all.filter((t) => {
              if (txFilter === 'all') return true;
              if (txFilter === 'in') return t.direction === 'in';
              if (txFilter === 'auto') return t.direction === 'out' && t.source === 'auto';
              if (txFilter === 'manual') return t.direction === 'out' && t.source !== 'auto';
              return true;
            });
            return (all.length === 0) ? (
            <div className="text-center py-10 text-white/40" data-testid="wallet-empty">
              <Wallet className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No earnings yet. Sales and tips will show up here.</p>
            </div>
          ) : txns.length === 0 ? (
            <div className="text-center py-8 text-white/40 text-sm" data-testid="wallet-filter-empty">Nothing here for this filter yet.</div>
          ) : (
            <div className="space-y-2" data-testid="wallet-transactions">
              {txns.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#12101A] border border-white/5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t.direction === 'out' ? 'bg-[#FF007F]/15' : 'bg-[#39FF14]/15'}`}>
                    {t.direction === 'out' ? <ArrowUpRight className="w-4 h-4 text-[#FF007F]" /> : <ArrowDownLeft className="w-4 h-4 text-[#39FF14]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate flex items-center gap-1.5">
                      {t.description || (t.type === 'withdrawal' ? 'Cash out' : t.type)}
                      {t.direction === 'out' && (
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${t.source === 'auto' ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'bg-white/10 text-white/60'}`} data-testid={`tx-source-${t.id}`}>
                          {t.source === 'auto' ? 'Auto' : 'Manual'}
                        </span>
                      )}
                    </p>
                    <p className="text-[#71717A] text-xs capitalize">
                      {t.type} · {fmtDate(t.created_at)}
                      {t.direction === 'out' && t.status && (
                        <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                          t.status === 'paid' ? 'bg-[#39FF14]/15 text-[#39FF14]'
                          : t.status === 'failed' ? 'bg-[#FF007F]/15 text-[#FF007F]'
                          : 'bg-amber-500/15 text-amber-400'
                        }`} data-testid={`tx-status-${t.id}`}>
                          {t.status === 'paid' ? 'Paid' : t.status === 'failed' ? 'Failed' : 'On the way'}
                        </span>
                      )}
                    </p>
                  </div>
                  <span className={`font-semibold text-sm ${t.direction === 'out' ? 'text-[#FF007F]' : 'text-[#39FF14]'}`}>
                    {t.direction === 'out' ? '-' : '+'}{money(t.amount)}
                  </span>
                </div>
              ))}
            </div>
          );
          })()}
        </div>
      </div>

      {/* Cash out modal */}
      {showCashOut && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setShowCashOut(false)} />
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="relative w-full max-w-sm bg-[#12101A] rounded-3xl border border-white/10 p-6" data-testid="cash-out-modal">
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded' }}>Cash Out</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">Available: <span className="text-white font-medium">{money(wallet?.available)}</span></p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg">$</span>
              <input type="number" min={wallet?.min_cashout} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-lg outline-none focus:border-[#00F0FF]/50"
                data-testid="cash-out-amount" placeholder="0.00" />
            </div>
            <p className="text-[#71717A] text-xs mt-2">Minimum {money(wallet?.min_cashout)}. Funds go to your linked bank via Stripe (payout fees, if any, are deducted by Stripe).</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowCashOut(false)} className="flex-1 py-3 rounded-xl border border-white/10 text-white hover:bg-white/5 transition-colors" data-testid="cash-out-cancel">Cancel</button>
              <button onClick={doCashOut} disabled={cashingOut} data-testid="cash-out-confirm"
                className="flex-1 py-3 rounded-xl bg-[#39FF14] text-[#050505] font-semibold hover:bg-[#39FF14]/90 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
                {cashingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5" /> Confirm</>}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;
